import React, { ChangeEvent, PureComponent } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { FontIconSpan, getFontIconKey } from '../../components/FontIcon';
import { Select } from '../../components/Form';
import { NavigationLink, NavigationList } from '../../components/Navigation';
import {
  LanguageIcon,
  NavigationListItemMobile,
  NavigationMobile
} from '../../components/NavigationMobile';
import { getSvgIconKey, SvgIcon } from '../../components/SvgIcon';
import { BookmarkApi, BookmarkContext } from '../../provider/BookmarkProvider';
import {
  withLang,
  WithLangProps
} from '../../provider/LanguageProvider/withLang';
import {
  selectLayer,
  selectMenu,
  selectSearchDetailVehicleId
} from '../../provider/Typo3Provider/selectors';
import { MenuEntry } from '../../provider/Typo3Provider/types';
import { getVehicleDetailSlug } from '../../provider/Typo3Provider/utils';
import {
  withTypo3,
  WithTypo3Props
} from '../../provider/Typo3Provider/withTypo3';

interface MetaNavigationContainerProps {
  onClickChild: () => void;
}

interface MetaNavigationContainerState {}

const noop = () => {};

const MetaNavigationListItemMobile = styled(NavigationListItemMobile)`
  ${LanguageIcon}, ${SvgIcon} {
    pointer-events: none;
  }
`;

class MetaNavigationContainer extends PureComponent<
  MetaNavigationContainerProps &
    WithTypo3Props &
    WithLangProps &
    RouteComponentProps,
  MetaNavigationContainerState
> {
  public getWatchListEntry() {
    const [watchListEntry = null] = selectMenu(this.props.typo3, 'watchlist');
    return watchListEntry;
  }

  public getLayer() {
    return selectLayer(this.props.typo3);
  }

  public getLanguages(): MenuEntry[] {
    return selectMenu(this.props.typo3, 'languages');
  }

  public getMeta() {
    return selectMenu(this.props.typo3, 'meta');
  }

  public renderBookmarksEntry = ({ bookmarks }: BookmarkApi) => {
    const watchListEntry = this.getWatchListEntry();
    const { typo3 } = this.props;
    if (watchListEntry) {
      return this.renderMenuEntry(
        {
          title: `${typo3.translateTypo3('metaNav.bookmark')} (${bookmarks.length})`,
          link: watchListEntry.link,
          icon: 'favourits',
          layout: ''
        },
        20
      );
    }
    return null;
  };

  public handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const { history, onClickChild, typo3 } = this.props;
    const { value } = event.target;
    const vehicleId = selectSearchDetailVehicleId(typo3);
    const vehicleSlug = vehicleId ? getVehicleDetailSlug(this.props.typo3, vehicleId) : undefined;

    history.push(`${value}${vehicleSlug || ''}`);
    onClickChild();
  };

  public renderOptions(languages: MenuEntry[]) {
    const {
      typo3: { translateTypo3 },
      lang: { languageCode, countryCode }
    } = this.props;
    const locale = `${languageCode.toUpperCase()}-${countryCode.toLowerCase()}`;
    const pairs = languages.map(
      (entry): [string, MenuEntry] => [
        translateTypo3(`lang.${entry.languageUid || '-1'}`),
        entry
      ]
    );
    pairs.sort(([s0], [s1]) => s0.localeCompare(s1, locale));
    return pairs.map(([title, entry]) => (
      <option value={entry.link} key={title}>
        {title}
      </option>
    ));
  }

  public renderContactEntry = (
    menuEntry: Pick<MenuEntry, 'title' | 'link'>,
    index: number
  ) => {
    const { onClickChild } = this.props;

    return (
      <NavigationListItemMobile key={index}>
        <NavigationLink to={menuEntry.link} onClick={onClickChild}>
          <FontIconSpan icon={'calling_outline'}>
            {menuEntry.title}
          </FontIconSpan>
        </NavigationLink>
      </NavigationListItemMobile>
    );
  };

  public renderMenuEntry = (
    menuEntry: Pick<MenuEntry, 'title' | 'link' | 'icon' | 'layout'>,
    index: number
  ) => {
    const layer = this.getLayer();
    // additionally provided by Typo3
    const hasLayer = menuEntry.layout === 11 && !!layer;
    const iconKey = getFontIconKey(menuEntry.icon);
    const { onClickChild } = this.props;

    if (!hasLayer) {
      return (
        <NavigationListItemMobile key={index}>
          <NavigationLink to={menuEntry.link} onClick={onClickChild}>
            {iconKey ? (
              <FontIconSpan icon={iconKey}>{menuEntry.title}</FontIconSpan>
            ) : (
              <span>{menuEntry.title}</span>
            )}
          </NavigationLink>
        </NavigationListItemMobile>
      );
    }
    return null;
  };

  public renderLanguagesEntry = () => {
    const { lang } = this.props;
    const languages = this.getLanguages();
    const activeEntry = languages.find(entry => !!entry.active);
    const icon = getSvgIconKey(lang.languageCode) || 'de';

    return (
      <MetaNavigationListItemMobile>
        <Select>
          {/* TODO: set value */}
          <select
            name="language"
            onChange={this.handleChange}
            onBlur={noop}
            value={activeEntry ? activeEntry.link : ''}
          >
            {this.renderOptions(languages)}
          </select>
          <SvgIcon icon="arrow" />
          <LanguageIcon>
            <SvgIcon icon={icon} />
          </LanguageIcon>
        </Select>
      </MetaNavigationListItemMobile>
    );
  };

  public render() {
    const meta = this.getMeta();
    const contactPage = selectMenu(this.props.typo3, 'contactpage');

    return (
      <NavigationMobile meta>
        <NavigationList>
          <BookmarkContext.Consumer>
            {this.renderBookmarksEntry}
          </BookmarkContext.Consumer>
          {meta.map(this.renderMenuEntry)}
          {contactPage.map(this.renderContactEntry)}
          {this.renderLanguagesEntry()}
        </NavigationList>
      </NavigationMobile>
    );
  }
}

export default withRouter(withTypo3(withLang(MetaNavigationContainer)));
